import styled from 'styled-components';
import React, {createContext, useContext, useState} from "react";
import {useToast} from "@chakra-ui/react";
import {Award as LucideAward, Check, Ellipsis, Hand, Pin} from "lucide-react";
import classNames from 'classnames';
import {useLoggedIn, useRelativeTime, useStoreChildComments, useUsername} from "../store";
import {useCommentVote} from "../api/comment";
import {themecolors} from "../utils/themecolors";
import useFeedback from "../utils/use_feedback";
import trim from "../utils/trim";
import NavLink from "./nav_link";
import TextContent from "./text_content";
import {CommentTextInputBox} from "./create_comment";
import ProfilePicture from "./profile_picture";
import {TextButton} from "./button";
import {CommentVote} from "./vote";
import Sheet from "./bottom-sheet/sheet";
import CommentActionsSheet from "./bottom-sheet/comment_actions";
import {useSheetModal} from "./test/ModalContext";
import SignupPromptSheet from "./bottom-sheet/signup_prompt";
import RoleIndicator from "./role_indicator";
import SeparatorBullet from "./separator_bullet";
import CommunityLogo from './community_logo';
import ModerateContentSheet from "./bottom-sheet/moderate_content";
import CommentManageSheet from "./bottom-sheet/comment_manage";

const Child = styled.div``;
const Wrapper = styled.div`
  padding: ${({contextual}) => contextual ? '12px 6px' : '6px'};
  display: flex;
  border-bottom: ${({contextual, theme}) => contextual ? `1px solid ${theme.color.name === 'dark' ? themecolors.dark_background_4 : themecolors.lighter_border}` : 'none'};
`;
const Metadata = styled.div`
  display: flex;
  margin-bottom: ${({contextual}) => contextual ? '8px' : '4px'};
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
  align-items: center;
  font-size: ${({contextual}) => contextual ? '12px' : '13px'};
`;
const ContextualInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2px;
  font-size: 13px;
  flex-wrap: wrap;
`;
const ContextualLogo = styled.div`
  flex-shrink: 0;
  margin-left: 4px;
  img {
    width: 24px;
    height: 24px;
    border-radius: 50%;
  }
`;
const CommunityName = styled.div`
  font-weight: 600;
`;
const PostTitle = styled.div`
  margin-top: 2px;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_medium_impact_text};
`;
const Author = styled.div`
  font-weight: 600;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};`;
const Separator = styled.div`
  font-size: 8px;
  padding: 0 4px;
`
const Content = styled.div`
  font-size: 14px;
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  max-width: 100%;
`;

const Actions = styled.div`
  font-size: 13px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  font-weight: 600;
  white-space: nowrap;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
`;
const Action = styled.div`
  margin-right: 16px;
  cursor: pointer;
  display: flex;
`;
const More = styled.div`
  font-size: 14px;
  color:  ${({theme}) => theme.link_2};
  margin-left: 38px;
  padding: 4px 0 8px 0;
`;
const Edited = styled.div`
  font-size: 13px;
  padding-top: 4px;
  padding-bottom: 2px;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
`;
const NewUser = styled.div`
  display: flex;
  font-size: 11px;
  margin-left: 8px;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_low_impact_text : themecolors.light_low_impact_text};
`;
const Award = styled.div`
  display: flex;
  align-items: center;
  white-space: break-spaces;
`;
const Approved = styled.div`
  color: ${() => themecolors.green};
  margin: 0 8px;
  font-size: 13px;
`;
const Pinned = styled.div``;

const MAX_DEPTH = 4;
const MAX_CHILDREN = 4;

export const PostContext = createContext(null);

export const Comment2 = styled(({
                                    className,
                                    comment,
                                    showCommunity,
                                    contextual,
                                    depth,
                                    hideComments,
                                    postAuthor,
                                    markRead,
                                    markUnread,
                                    blocked
                                }) => {

    const {openModal} = useSheetModal();
    const loggedIn = useLoggedIn();
    const username = useUsername();
    const [collapsed, setCollapsed] = useState(comment?.is_deleted);

    const ago = useRelativeTime(comment?.created, false);

    const {feedbackCh} = useFeedback();
    const vote = useCommentVote(feedbackCh);

    const handleVote = async (dir) => {
        if (!loggedIn) {

        } else {
            await vote(comment, dir);
        }
    };

    const {comments} = useStoreChildComments(comment?.id);
    const postContext = useContext(PostContext);
    const uuid = comment?.uuid;
    const postLink = postContext?.link || `/c/${comment?.community}/p/${comment?.post_uuid}/x/c`;

    const trimDepth = comments && comments.length > 0 && typeof depth === 'number' && depth >= MAX_DEPTH;
    const trimChildren = !trimDepth && comments && postContext?.commentMap && !postContext.commentMap.get(comment?.id);
    const visibleComments = trimDepth ? [] : (trimChildren ? comments?.slice(0, MAX_CHILDREN) : comments);
    const hiddenComments = 
        (trimDepth ? comments : (trimChildren ? comments.slice(MAX_CHILDREN) : []))
            .length || 0;

    const [reply, setReply] = useState(false);
    const handleReply = (event) => {
        event.stopPropagation();
        if (!loggedIn) {
            const modalContent = ({ handleCloseModal }) => (
                <Sheet onClose={() => handleCloseModal() } padding={"header"}>
                    <SignupPromptSheet onClose={() => handleCloseModal() } />
                </Sheet>
            );
            openModal(modalContent);
        } else {
            setReply(!reply);
        }
    };

    const handleCollapse = () => {
        if (contextual) return;
        setCollapsed(!collapsed);
    }

    const toast = useToast();

    const handleActions = (event) => {
        event.stopPropagation();
        const modalContent = ({handleCloseModal}) => (
            <Sheet onClose={() => handleCloseModal()} padding={"menu"}>
                <CommentActionsSheet commentId={comment?.id} onClose={() => handleCloseModal()}/>
            </Sheet>
        );
        openModal(modalContent);
    };

    const handleMod = (event) => {
        event.stopPropagation();
        const modalContent = ({handleCloseModal}) => (
            <Sheet onClose={() => handleCloseModal() } padding={"menu"}><ModerateContentSheet comment={comment} onClose={() => handleCloseModal() } /></Sheet>
        );
        openModal(modalContent);
    };

    const handleEdit = (event) => {
        event.stopPropagation();
        const modalContent = ({handleCloseModal}) => (
            <Sheet onClose={() => handleCloseModal() } padding={"menu"}><CommentManageSheet commentId={comment?.id} onClose={() => handleCloseModal() } /></Sheet>
        );
        openModal(modalContent);
    };

    const reported = comment?.moderation && comment?.moderation?.is_reported;
    const removed = comment?.moderation && comment?.moderation?.removed_at !== 0;

    return (
        <div style={{marginLeft: depth > 1 && '24px', marginBottom: depth === 1 && "16px"}}>
            <Wrapper contextual={contextual} className={classNames(className, {removed, reported})}>
                {!contextual &&
                    <div>
                        <ProfilePicture
                            profilePicture={comment.profile_picture && `https://media.scored.co/profile/${comment.profile_picture}.jpg`}
                            size={20} fontSize={12} username={comment.author}/>
                    </div>
                }
                {contextual &&
                    <ContextualLogo>
                        <CommunityLogo name={comment.community} />
                    </ContextualLogo>
                }
                <div style={{
                    marginLeft: '8px', 
                    flexGrow: 1,
                }}>
                    {contextual && comment?.post_title && (
                        <>
                            <ContextualInfo>
                                <CommunityName>
                                    <NavLink href={`/c/${comment.community}/`} react_route>{comment.community}</NavLink>
                                </CommunityName>
                                <SeparatorBullet />
                                <PostTitle>
                                <NavLink href={`/c/${comment.community}/p/${comment.post_uuid}/`} react_route>
                                    {trim(comment.post_title, 96)}
                                </NavLink>
                            </PostTitle>
                            </ContextualInfo>
                        </>
                    )}
                    <div>
                        <Metadata contextual={contextual} onClick={handleCollapse}>
                            <Author>
                                {comment.author ?
                                    <NavLink href={`/u/${comment.author}/`} react_route>{comment.author}</NavLink>
                                    : "[deleted]"}
                            </Author>
                            <RoleIndicator
                                mod={comment.is_moderator}
                                admin={comment.is_admin}
                                op={postAuthor && comment.author === postAuthor}
                                proTier={comment.pro_tier}
                            />
                            <Separator>•</Separator>
                            <NavLink href={`${postLink}/${comment?.uuid}`} react_route>{ago} ago</NavLink>
                            {comment?.is_new_user && (
                                <NewUser>
                                    <Hand size={14} />
                                </NewUser>
                            )}
                            {comment?.awards > 0 &&
                                <Award>
                                    <LucideAward size={16} color={"#dcba00"}/>
                                </Award>
                            }

                            {comment?.moderation && comment?.moderation?.approved_at !== 0 &&
                                <Approved><Check size={16} /></Approved>
                            }

                            {comment?.is_stickied &&
                                <Pinned><Pin size={14} color={"#ffbe00"} fill={"#ffbe00"} /></Pinned>
                            }

                        </Metadata>
                        {!collapsed &&
                            <Content>
                                {comment && !comment.is_deleted &&
                                <TextContent removed={comment.is_removed} deleted={comment.is_deleted} content={comment.content}
                                             type={"Comment"} removalSource={comment?.removal_source}/>}
                            </Content>
                        }
                        {!collapsed && comment.is_edited &&
                            <Edited>(edited)</Edited>
                        }
{/*                        {edit &&
                            <CommentEditTextInputBox
                                comment={comment}
                                onCreated={() => {
                                    if (!postContext) {
                                        toast({
                                            title: 'Your comment has been updated!',
                                            status: 'success',
                                            isClosable: false,
                                        });
                                    }
                                }}
                                onClose={() => setEdit(false)}
                            />
                        }*/}
                        {!collapsed &&
                        <Actions>
                            <Action>
                                <CommentVote onVote={handleVote} vote={comment?.vote_state} score={comment?.score}/>
                            </Action>
                            <Action onClick={handleReply}>
                                Reply
                            </Action>
                            {comment?.author === username &&
                                <Action onClick={handleEdit}>
                                    Edit
                                </Action>
                            }
                            {comment?.moderation &&
                                <Action onClick={handleMod}>
                                    Mod
                                </Action>
                            }
                            <Action onClick={handleActions}>
                                <Ellipsis size={14}/>
                            </Action>
                        </Actions>
                        }
                        {reply && (
                            <CommentTextInputBox
                                comment={comment}
                                onCreated={() => {
                                    if (!postContext) {
                                        toast({
                                            title: 'Your comment has been posted!',
                                            status: 'success',
                                            isClosable: false,
                                        });
                                    }
                                }}
                                onClose={() => setReply(false)}
                            />
                        )}

                    </div>

                </div>

            </Wrapper>

            {!contextual && !collapsed && visibleComments?.map((comment) =>
                !blocked?.includes(comment?.author) && comment?.is_shown && (
                    <Child key={comment.id} className='child'>
                        <Comment2 comment={comment} parentUuid={uuid} depth={depth + 1} postAuthor={postAuthor}
                                  blocked={blocked}/>
                    </Child>
                )
            )}

            {!contextual && !collapsed && trimChildren && hiddenComments > 0 && (
                <More>
                    <TextButton onClick={() => postContext.expandComment(comment.id)}>
                        Show {hiddenComments} more comment{hiddenComments === 1 ? '' : 's'}
                    </TextButton>
                </More>
            )}
            {!contextual && !collapsed && trimDepth && hiddenComments > 0 && (
                <More>
                    <NavLink href={`${postLink}/${comment?.uuid}`} react_route>
                        Load {hiddenComments} more repl{hiddenComments === 1 ? 'y' : 'ies'}
                    </NavLink>
                </More>
            )}

        </div>
    )
})`
  &.removed {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.pink_2 : theme.pink_1};
  }
  &.reported {
    background-color: ${({theme}) => theme.color.name === 'dark' ? theme.yellow_2 : theme.yellow_1};
  }
`;

export default Comment2;
